import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { apiError, setCsrf } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null); // null=loading, false=logged out, object=session
  const [notifs, setNotifs] = useState([]);

  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setCsrf(data.csrf);
      setSession(data);
      return data;
    } catch {
      setSession(false);
      return false;
    }
  }, []);

  const refreshNotifs = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifs(data);
    } catch (e) { console.debug("notifs refresh failed", e); }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  useEffect(() => {
    if (session && session.tenant) refreshNotifs();
  }, [session, refreshNotifs]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.mfa_required) return { mfaRequired: true, challenge: data.challenge, email: data.email };
    localStorage.removeItem("azx_act_as");
    await loadMe();
    return { mfaRequired: false };
  };

  const verifyOtp = async (challenge, code) => {
    await api.post("/auth/verify-otp", { challenge, code });
    localStorage.removeItem("azx_act_as");
    return await loadMe();
  };

  const resendOtp = async (challenge) => {
    await api.post("/auth/resend-otp", { challenge });
  };

  const register = async (payload) => {
    await api.post("/auth/register", payload);
    return await loadMe();
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.debug("logout call failed", e); }
    localStorage.removeItem("azx_act_as");
    setCsrf(null);           // drop the in-memory CSRF token
    setSession(false);
  };

  const value = { session, setSession, login, register, logout, loadMe, verifyOtp, resendOtp,
                  notifs, refreshNotifs, apiError };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
